import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, PaperPlaneRight, Microphone } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import VoiceBubble from "../components/VoiceBubble";
import VoiceRecorderSheet from "../components/VoiceRecorderSheet";

export default function Thread() {
  const { peerId } = useParams();
  const { user } = useAuth();
  const [peer, setPeer] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [pickVoice, setPickVoice] = useState(false);
  const bottomRef = useRef();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/messages/${peerId}`);
      setPeer(data.peer); setMsgs(data.messages);
    } catch (err) {
      console.error("thread load failed", err);
    }
  }, [peerId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const { data } = await api.post("/messages", { recipient_id: peerId, text });
      setMsgs((m) => [...m, data]);
      setText("");
    } finally { setSending(false); }
  };

  const sendVoice = async (clip) => {
    setPickVoice(false);
    try {
      const { data } = await api.post("/messages", { recipient_id: peerId, text: "", voice_path: clip.path, voice_duration: clip.duration, voice_title: clip.title });
      setMsgs((m) => [...m, data]);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not send voice clip"); }
  };

  return (
    <div className="h-[100dvh] flex flex-col bg-ink">
      <header className="crystal-glass px-4 py-4 flex items-center gap-3">
        <Link to="/messages" className="p-2 -ml-2 text-cream"><ArrowLeft size={22} /></Link>
        {peer && (
          <>
            <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-papaya to-sea flex items-center justify-center text-ink font-black">
              {peer.avatar_path ? <img src={fileUrl(peer.avatar_path)} alt="" className="w-full h-full object-cover" /> : (peer.display_name || "?")[0]?.toUpperCase()}
            </div>
            <div>
              <p className="text-cream font-bold">{peer.display_name}</p>
              <p className="text-cream/50 text-xs">@{peer.username}</p>
            </div>
          </>
        )}
      </header>

      <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="thread-messages">
        {msgs.map((m) => {
          const mine = m.sender_id === user.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`thread-msg-${m.id}`}>
              {m.voice_path ? (
                <VoiceBubble url={m.voice_url || `/api/files/${m.voice_path}`} duration={m.voice_duration} title={m.voice_title} mine={mine} testid={`thread-voice-${m.id}`} />
              ) : (
                <div className={`max-w-[70%] px-4 py-2.5 rounded-2xl ${mine ? "bg-papaya text-ink rounded-br-md" : "bg-white/5 text-cream rounded-bl-md border border-white/5"}`}>
                  <p className="text-sm leading-relaxed">{m.text}</p>
                </div>
              )}
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={send} className="p-4 pr-24 sm:pr-4 border-t border-white/5 flex gap-2 mb-32 sm:mb-20">
        <button type="button" onClick={() => setPickVoice(true)} data-testid="thread-voice-btn" title="Send a voice clip" className="w-12 h-12 rounded-full bg-white/5 border border-white/10 text-papaya flex items-center justify-center flex-shrink-0">
          <Microphone size={20} weight="fill" />
        </button>
        <input
          data-testid="thread-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Send a message…"
          className="flex-1 bg-white/5 border border-white/10 rounded-full px-5 py-3 text-cream placeholder:text-cream/40 focus:outline-none focus:border-papaya"
        />
        <button data-testid="thread-send" type="submit" disabled={!text.trim() || sending} className="w-12 h-12 rounded-full bg-papaya hover:bg-papaya-hover text-ink flex items-center justify-center disabled:opacity-40">
          <PaperPlaneRight size={20} weight="fill" />
        </button>
      </form>
      <VoiceRecorderSheet open={pickVoice} onClose={() => setPickVoice(false)} onPick={sendVoice} title="Voice message" />
    </div>
  );
}
